import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const RefreshContext = createContext({
  refreshPage: () => {},
  checkForUpdates: () => {},
  hasNewContent: false,
  isRefreshing: false,
  lastRefresh: null,
  themeVersion: 0,
  contentUpdates: {
    messages: 0,
    interests: 0,
    profiles: 0
  }
});

export const useRefresh = () => useContext(RefreshContext);

export const RefreshProvider = ({ children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasNewContent, setHasNewContent] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [themeVersion, setThemeVersion] = useState(0);
  const [contentUpdates, setContentUpdates] = useState({
    messages: 0,
    interests: 0,
    profiles: 0
  });
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const { toast } = useToast();
  
  const lastThemeCheck = useRef(null);
  const pollInterval = useRef(null);

  // Check for theme/design changes from admin
  const checkThemeChanges = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/theme-settings/public`);
      if (response.ok) {
        const data = await response.json();
        const newVersion = data.updated_at || data.preset || 'default';
        
        if (lastThemeCheck.current && lastThemeCheck.current !== newVersion) {
          // Theme changed! Show notification
          setShowUpdateBanner(true);
          toast({
            title: "🎨 Website Updated!",
            description: "The site design has changed. Click refresh to see the new look.",
          });
        }
        lastThemeCheck.current = newVersion;
      }
    } catch (error) {
      console.error('Failed to check theme:', error);
    }
  }, [toast]);

  // Check for new content (messages, interests, etc.)
  const checkForUpdates = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Check unread messages count
      const messagesRes = await fetch(`${API_URL}/api/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Check pending interests count
      const interestsRes = await fetch(`${API_URL}/api/interests/pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let newMessages = 0;
      let newInterests = 0;

      if (messagesRes.ok) {
        const msgData = await messagesRes.json();
        newMessages = msgData.count || 0;
      }

      if (interestsRes.ok) {
        const intData = await interestsRes.json();
        newInterests = intData.count || 0;
      }

      // Check if there's new content since last refresh
      const hasNew = newMessages > contentUpdates.messages || 
                     newInterests > contentUpdates.interests;

      if (hasNew) {
        setHasNewContent(true);
        setContentUpdates({
          messages: newMessages,
          interests: newInterests,
          profiles: 0
        });
      }
    } catch (error) {
      console.error('Failed to check updates:', error);
    }
  }, [contentUpdates]);

  // Full page refresh
  const refreshPage = useCallback(async (showToast = true) => {
    setIsRefreshing(true);
    
    try {
      // Clear any cached data
      setHasNewContent(false);
      setShowUpdateBanner(false);
      setLastRefresh(Date.now());
      
      // Reload the page to get fresh content
      window.location.reload();
    } catch (error) {
      console.error('Refresh failed:', error);
      if (showToast) {
        toast({
          title: "Refresh Failed",
          description: "Please try again",
          variant: "destructive"
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [toast]);

  // Soft refresh (just update data without page reload)
  const softRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setHasNewContent(false);
    setShowUpdateBanner(false);
    setLastRefresh(Date.now());
    
    // Trigger a re-fetch event that components can listen to
    window.dispatchEvent(new CustomEvent('app-refresh'));
    
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "✓ Refreshed",
        description: "Content updated",
      });
    }, 500);
  }, [toast]);

  // Start polling for updates
  useEffect(() => {
    // Check theme changes every 30 seconds
    const themeInterval = setInterval(checkThemeChanges, 30000);
    
    // Check for content updates every 60 seconds
    const contentInterval = setInterval(checkForUpdates, 60000);
    
    // Initial check
    checkThemeChanges();
    checkForUpdates();

    return () => {
      clearInterval(themeInterval);
      clearInterval(contentInterval);
    };
  }, [checkThemeChanges, checkForUpdates]);

  // Listen for visibility changes (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to tab, check for updates
        checkThemeChanges();
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkThemeChanges, checkForUpdates]);

  return (
    <RefreshContext.Provider value={{
      refreshPage,
      softRefresh,
      checkForUpdates,
      hasNewContent,
      isRefreshing,
      lastRefresh,
      themeVersion,
      contentUpdates,
      showUpdateBanner,
      setShowUpdateBanner
    }}>
      {children}
    </RefreshContext.Provider>
  );
};

export default RefreshContext;
